// src/lib/training/runTrainingAsk.ts
import type { KbLane } from "../kb/types";
import { isLocationLookupIntent, fetchSalesLocations, type LocationResult } from "../contacts/router";
import { formatLocationAnswer } from "../contacts/locationFormat";
import type { AskContext, AskResult } from "./trainingAskTypes";
import { isDirectContactIntent } from "./trainingPanelLogic";
import { runTrainingKbPhase, syntheticNoAnswerKbPhase } from "./trainingAskKbPhase";
import { runTrainingContactsAndFinalize } from "./trainingAskContactsAndFinalize";

export type { AskContext, AskResult } from "./trainingAskTypes";

export async function runTrainingAsk(q: string, ctx: AskContext): Promise<AskResult> {
  const requestedLane = (ctx.laneOverride || ctx.lane || "answers") as KbLane;
  console.log("[training/ask][debug] runTrainingAsk:start", {
    queryPreview: (q || "").slice(0, 120),
    requestedLane,
    lane: ctx.lane || null,
    laneOverride: ctx.laneOverride || null,
    organizationId: ctx.organizationId || null,
    kbLocationId: ctx.kbLocationId || null,
    queryKind: ctx.queryKind || "new",
  });
  const laneMatchedTurns = (ctx.threadContextTurns || []).filter((turn) => (turn.lane || "answers") === requestedLane);
  const crossLaneTurns = (ctx.threadContextTurns || []).filter((turn) => (turn.lane || "answers") !== requestedLane);
  const contextTurns = requestedLane === "answers" ? laneMatchedTurns.slice(-4) : [...laneMatchedTurns.slice(-4), ...crossLaneTurns.slice(-2)];
  const contextualQuery =
    contextTurns.length > 0
      ? [
          `Current user question: ${q}`,
          `Active lane: ${requestedLane}`,
          "",
          "Prior same-lane context first, then cross-lane references:",
          ...contextTurns.map((turn, idx) => {
            const laneLabel = turn.lane || "answers";
            return `#${idx + 1} [${turn.mode} | lane=${laneLabel}] Q: ${turn.question}\nA: ${turn.answer}`;
          }),
        ].join("\n")
      : q;
  const topK = ctx.topK ?? 8;
  const contactLimit = ctx.contactLimit ?? 3;
  const contactFetchLimit = ctx.contactFetchLimit ?? 10;

  const guidancePurpose = ctx.guidancePurpose ?? "policy";
  const guidanceProduct = ctx.guidanceProduct ?? "all";
  const guidanceSource = ctx.guidanceSource ?? "default";

  if (requestedLane === "contacts" && isLocationLookupIntent(q)) {
    console.log("[training/ask][debug] contacts-location-intent branch hit", {
      requestedLane,
      queryPreview: (q || "").slice(0, 120),
    });
    const loc = await fetchSalesLocations(q, 25);
    let mapped = { answer: "", hits: [] as any[] };
    let locationCards: LocationResult[] = [];
    let locationError: string | null = null;
    let needsClarification = false;

    if (loc.ok) {
      locationCards = loc.results || [];
      mapped = { answer: formatLocationAnswer(q, locationCards), hits: [] };
      needsClarification = false;
    } else {
      locationCards = [];
      locationError = loc.error;
      mapped = { answer: `Couldn’t load location contacts right now. (${loc.error})`, hits: [] };
      needsClarification = false;
    }

    return {
      ok: true,
      mapped,
      needsClarification,
      contacts: [],
      contactsWhy: "",
      contactsError: null,
      locationCards,
      locationError,
      speakText: null,
      answerMode: "location",
      guidancePurpose,
      guidanceProduct,
      guidanceSource,
      requestedLane,
      effectiveLane: requestedLane,
    };
  }

  // Contacts lane + explicit “who do I talk to…” style questions: try directory first so KB policy chunks cannot speak over routing.
  if (
    requestedLane === "contacts" &&
    isDirectContactIntent(q, isLocationLookupIntent) &&
    !isLocationLookupIntent(q)
  ) {
    console.log("[training/ask][debug] contacts-direct-intent prefetch branch hit", {
      requestedLane,
      queryPreview: (q || "").slice(0, 120),
    });
    const kbSynth = syntheticNoAnswerKbPhase(ctx);
    const contactFirst = await runTrainingContactsAndFinalize({
      q,
      ctx,
      contactLimit,
      contactFetchLimit,
      requestedLane,
      guidancePurpose,
      guidanceProduct,
      guidanceSource,
      kb: kbSynth,
    });
    if ((contactFirst.contacts?.length ?? 0) > 0) return contactFirst;
  }

  const kb = await runTrainingKbPhase({
    q,
    contextualQuery,
    ctx,
    requestedLane,
    topK,
    contactLimit,
    contactFetchLimit,
  });
  console.log("[training/ask][debug] after KB phase", {
    requestedLane,
    kbMode: kb.out?.mode || null,
    kbSources: kb.out?.sources?.length ?? 0,
    kbContactsSeeded: kb.contacts?.length ?? 0,
    kbContactsError: kb.contactsError || null,
    answerMode: kb.answerMode,
  });

  return runTrainingContactsAndFinalize({
    q,
    ctx,
    contactLimit,
    contactFetchLimit,
    requestedLane,
    guidancePurpose,
    guidanceProduct,
    guidanceSource,
    kb,
  });
}
